/**
 * The curated public categories, read from the `blog-filter-categories` CMS model.
 *
 * Blocks store a category's stable `key`, while articles reference the category entry itself,
 * so the ids are looked up here to bridge the two. Like [@/api/cms/knowledgeHubArticles] this
 * asks the CMS directly, and is expected to move to the Ecommerce Search content scope once
 * that can deliver the categories as facets.
 *
 * Serves both the running site, through [useFilterCategoriesQuery], and the Builder editor's
 * category dropdown, through [getFilterCategoriesForEditor]. The editor asks before React is
 * involved, so the two share this one request rather than each bringing its own transport.
 */
import { Builder, builder } from '@builder.io/react';
import { BuilderContent } from '@builder.io/sdk';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/api/apiConfig';
import { SettingsResponse } from '@/generated/data.schemas';
import { CmsFilterCategoryEntry } from '@/types/filterCategory';

const filterCategoriesModelName = 'blog-filter-categories';
const filterCategoryFields = ['id', 'data.key', 'data.name'].join(',');
const filterCategoriesLimit = 100;

/**
 * Component registration runs before `builder.init`, so the key the HTML puts on the window
 * is the only one available to the editor, see BootClient.
 */
const getApiKey = (): string => {
	if (builder.apiKey) {
		return builder.apiKey;
	}

	const fromWindow =
		typeof window !== 'undefined' ? (window as unknown as Record<string, unknown>).__BUILDER_PUBLIC_API_KEY__ : '';
	return typeof fromWindow === 'string' ? fromWindow : '';
};

const trimmedString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * Entries without a key are dropped: the key is the only thing blocks and queries match on,
 * so a keyless category can never be selected or found.
 */
const mapCmsFilterCategory = (entry: BuilderContent | null | undefined): CmsFilterCategoryEntry | null => {
	const key = trimmedString(entry?.data?.key);
	const id = trimmedString(entry?.id);
	if (key.length === 0 || id.length === 0) {
		return null;
	}

	const name = trimmedString(entry?.data?.name);
	// A category without a name is still usable, its key is then the least confusing label
	return { id, key, name: name.length > 0 ? name : key };
};

/**
 * Asked of the content API directly, for the same reason as the articles: the SDK always sends
 * user attributes, which makes the CMS apply targeting to a query meant to span a whole model.
 */
const fetchFilterCategories = async (locale: string): Promise<CmsFilterCategoryEntry[]> => {
	const apiKey = getApiKey();
	if (apiKey.length === 0) {
		return [];
	}

	const url = new URL(`https://cdn.builder.io/api/v3/content/${filterCategoriesModelName}`);
	url.searchParams.set('apiKey', apiKey);
	url.searchParams.set('limit', String(filterCategoriesLimit));
	url.searchParams.set('fields', filterCategoryFields);
	if (locale.length > 0) {
		url.searchParams.set('locale', locale);
	}
	// Editors need a category they just added to show up straight away, visitors can have the cached list
	if (Builder.isEditing) {
		url.searchParams.set('cachebust', 'true');
	}

	const response = await fetch(url.toString());
	if (!response.ok) {
		throw new Error(`Content API answered ${response.status} for ${filterCategoriesModelName}`);
	}

	const payload = (await response.json()) as { readonly results?: BuilderContent[] };
	// Keyed, so a duplicated key in the model cannot produce two entries for one category
	const categoriesByKey = new Map<string, CmsFilterCategoryEntry>();

	for (const entry of payload.results ?? []) {
		const category = mapCmsFilterCategory(entry);
		if (category && !categoriesByKey.has(category.key)) {
			categoriesByKey.set(category.key, category);
		}
	}

	return Array.from(categoriesByKey.values()).sort((left, right) =>
		left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }),
	);
};

export const useFilterCategoriesQuery = (
	settings: SettingsResponse | undefined,
): UseQueryResult<CmsFilterCategoryEntry[]> => {
	const cultureCode = settings?.cultureCode ?? '';

	return useQuery({
		queryKey: queryKeys.cms.blogFilterCategories({ locale: cultureCode }).queryKey,
		queryFn: async () => {
			try {
				return await fetchFilterCategories(cultureCode);
			} catch (error) {
				console.error(`Error fetching ${filterCategoriesModelName} for locale ${cultureCode}`, error);
				return [];
			}
		},
		enabled: !!settings,
	});
};

// ──────────────────────────────────────────────────────────────────────────────
// Builder editor registration
// ──────────────────────────────────────────────────────────────────────────────

let editorCategoriesCache: CmsFilterCategoryEntry[] = [];
let editorCategoriesPromise: Promise<CmsFilterCategoryEntry[]> | null = null;

export const areSameFilterCategories = (
	left: readonly CmsFilterCategoryEntry[],
	right: readonly CmsFilterCategoryEntry[],
): boolean => {
	if (left.length !== right.length) {
		return false;
	}

	for (let i = 0; i < left.length; i++) {
		if (left[i].key !== right[i].key || left[i].name !== right[i].name) {
			return false;
		}
	}

	return true;
};

/** Whatever has been fetched so far, for a synchronous first registration */
export const getCachedFilterCategories = (): readonly CmsFilterCategoryEntry[] => editorCategoriesCache;

/**
 * The editor shows every category regardless of locale, since a block is configured once
 * and the picked keys are what the article query matches on.
 */
export const getFilterCategoriesForEditor = (): Promise<CmsFilterCategoryEntry[]> => {
	if (editorCategoriesPromise) {
		return editorCategoriesPromise;
	}

	editorCategoriesPromise = fetchFilterCategories('')
		.then((categories) => {
			editorCategoriesCache = categories;
			return categories;
		})
		.catch((error: unknown) => {
			console.warn(`[BlogOverview] Could not load ${filterCategoriesModelName} for Builder`, error);
			// Cleared so a later registration can retry instead of being stuck with an empty list
			editorCategoriesPromise = null;
			return editorCategoriesCache;
		});

	return editorCategoriesPromise;
};
